import { Controller } from '@hotwired/stimulus'

export default class DashboardChartsRentalController extends Controller {
  static targets = [
    'statusesChart',
    'projectTypeChart',
    'repaymentsChart',
    'statusesChartLoader',
    'projectTypeChartLoader',
    'repaymentsChartLoader',
    'profileId',
  ];

  static values = {
    url: String,
    singularSuffix: String,
    pluralSuffix: String,
  };

  connect() {
    this.requestChartsData();
  }

  requestChartsData() {
    const controller = this;

    App.cable.subscriptions.create(
      {
        channel: 'DashboardChartsChannel',
        profile_id: controller.profileIdTarget.dataset['profileId'],
      },
      {
        received(data) {
          if (data.status == 'success') {
            JSON.parse(data.charts_data).forEach((chartData) => {
              controller.renderChart(chartData);
            });
          } else {
            controller.statusesChartLoaderTarget.remove();
            controller.projectTypeChartLoaderTarget.remove();
            controller.repaymentsChartLoaderTarget.remove();
            toastr.error(
              'Une erreur est survenue lors du chargement de vos données veuillez recharger la page.'
            );
          }

          this.unsubscribe();
        },
        disconnected(data) {
          this.unsubscribe();
        },
      }
    );
  }

  renderChart(chartData) {
    const chartTarget = this.chartTargetFor(chartData.name);
    const chartLoaderTarget = this.chartLoaderTargetFor(chartData.name);

    if (!chartTarget) {
      return;
    }

    const existingChart = Chart.getChart(chartTarget);

    if (!!existingChart) {
      existingChart.destroy();
    }

    chartLoaderTarget.remove();

    new Chart(chartTarget, {
      type: chartData.type,
      data: chartData,
      options: this.chartOptionsFor(chartData.name),
      plugins: this.chartPluginsFor(chartData.name),
    });
  }

  chartTargetFor(chartName) {
    switch (chartName) {
      case 'project-statuses-chart':
        return this.statusesChartTarget;
      case 'project-type-chart':
        return this.projectTypeChartTarget;
      case 'repayments-chart':
        return this.repaymentsChartTarget;
    }
  }

  chartLoaderTargetFor(chartName) {
    switch (chartName) {
      case 'project-statuses-chart':
        return this.statusesChartLoaderTarget;
      case 'project-type-chart':
        return this.projectTypeChartLoaderTarget;
      case 'repayments-chart':
        return this.repaymentsChartLoaderTarget;
    }
  }

  chartOptionsFor(chartName) {
    switch (chartName) {
      case 'repayments-chart':
        return this.barChartOptions();
      default:
        return this.pieChartOptions(chartName);
    }
  }

  chartPluginsFor(chartName) {
    switch (chartName) {
      case 'repayments-chart':
        return [];
      default:
        return [PieCustomLegendPluginRebranding.htmlLegendPlugin()];
    }
  }

  pieChartOptions(chartName) {
    const controller = this;

    return {
      plugins: {
        htmlLegend: {
          containerID: `${chartName}-legend`,
        },
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const suffix =
                context.formattedValue <= 1 ? controller.singularSuffixValue : controller.pluralSuffixValue;

              return `${context.formattedValue} ${suffix}`;
            },
          },
        },
      },
      aspectRatio: 1.5,
      responsive: true,
      cutout: '75%',
    };
  }

  barChartOptions() {
    const controller = this;

    return {
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 4,
            boxHeight: 4,
            padding: 16,
            usePointStyle: true,
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              return controller.labelWithCurrency(context);
            },
          },
        },
      },
      interaction: {
        intersect: true,
      },
      aspectRatio: 1.5,
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            callback: function (val) {
              return this.getLabelForValue(val).slice(0, -1);
            },
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          stacked: true,
          grid: {
            lineWidth: 0.5,
          },
          max: function (context) {
            const defaultMaxAmount = 2000;

            const defaultAmountNeeded = context.chart.data.datasets.every(
              function (dataset) {
                return dataset.data.every(function (val) {
                  return parseFloat(val) === 0;
                });
              }
            );

            if (defaultAmountNeeded) {
              return defaultMaxAmount;
            }
          },
        },
      },
      responsive: true,
    };
  }

  labelWithCurrency(context) {
    const label = context.dataset.label || '';

    if (label) {
      return `${label}: ${context.formattedValue} €`;
    } else {
      return `${context.formattedValue} €`;
    }
  }
}
